import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { Inbox } from 'entities/Inbox/model/Inbox'
import type { IResponseInbox } from 'entities/Inbox/api/types'
import type { InboxesStore } from 'entities/Inbox/store/inboxes'
import type { EntitiesStore } from 'entities/EntitiesExport'

export class OrganizationInboxesStore {
  private _organizationInboxes: Map<number, Inbox> = new Map()
  private _loading = false
  private _loaded = false

  initPromise: Promise<unknown>
  private _setFinishInit: ((value?: unknown) => void) | null = null

  constructor(private _inboxesStore: InboxesStore, private _entities: EntitiesStore) {
    this.initPromise = new Promise((resolve) => {
      this._setFinishInit = resolve
    })

    makeAutoObservable(this)
  }

  syncItem = (data: IResponseInbox) => {
    const existing = this._organizationInboxes.get(data.id)
    if (existing) {
      existing.syncOrigin({ ...existing, ...data })
      return
    }

    const personalInbox = this._inboxesStore.getItem(data.id)

    if (personalInbox instanceof Inbox) {
      this._organizationInboxes.set(data.id, personalInbox)
    } else {
      this._organizationInboxes.set(data.id, new Inbox(data, this._entities))
    }
  }

  deleteItem = (id: number) => {
    this._organizationInboxes.delete(id)
  }

  load = async () => {
    this._loading = true
    try {
      const { data } = await this._inboxesStore.getTeamsAll()

      runInAction(() => {
        data.forEach((item) => {
          this._entities.numbersStore.addItems(item.numbers)
          const existing = this._inboxesStore.inboxesMap.get(item.id)
          if (existing instanceof Inbox) {
            existing.syncOrigin({ ...existing, ...item })
            this._organizationInboxes.set(item.id, existing)
          } else {
            this._organizationInboxes.set(item.id, new Inbox(item, this._entities))
          }
        })
        this._loaded = true
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
        this._setFinishInit?.()
      })
    }
  }

  getItem = (id: number): Inbox | undefined => {
    return this._organizationInboxes.get(id)
  }

  get items(): Inbox[] {
    return Array.from(this._organizationInboxes.values())
  }

  get loading(): boolean {
    return this._loading
  }

  get loaded(): boolean {
    return this._loaded
  }

  reset = () => {
    this._organizationInboxes.clear()
    this._loaded = false
    this._loading = false
    this.initPromise = new Promise((resolve) => {
      this._setFinishInit = resolve
    })
  }
}
